"use client";

import { useState } from "react";
import { Download, CheckCircle2, Loader2 } from "lucide-react";
import { trackEvent, trackDownload } from "@/lib/analytics";
import { defaultLocale, type Locale } from "@/lib/i18n";
import MarketingConsent from "@/components/forms/MarketingConsent";
import {
  MARKETING_CONSENT_TEXT,
  appendMarketingConsent,
  withMarketingConsentLine,
} from "@/lib/marketing-consent";

const PDF_PATH = "/downloads/chatgpt-plan-report-2026.pdf";
const PDF_FILENAME = "Anchor-Sprint-ChatGPT-Plan-Report-2026.pdf";
const SOURCE = "chatgpt-plans-malaysia-guide";

type Status = "idle" | "loading" | "success" | "error";

interface Copy {
  title: string;
  desc: string;
  name: string;
  email: string;
  submit: string;
  sending: string;
  successTitle: string;
  successBefore: string;
  successLink: string;
  successAfter: string;
  privacy: string;
  consentLabel: string;
  privacyNotice: string;
  errNotConfigured: string;
  errGeneric: string;
}

const STRINGS: Record<Locale, Copy> = {
  en: {
    title: "Download the ChatGPT plan report (PDF)",
    desc: "Every ChatGPT plan compared, from Free to Enterprise and the API: price in USD and RM, seats, admin and security controls, and data-training defaults, in a two-page report for IT and procurement. Enter your details and we'll start the download.",
    name: "Your name",
    email: "Work email",
    submit: "Get the report",
    sending: "Sending…",
    successTitle: "Your download is on its way",
    successBefore: "The ChatGPT plan report should be downloading now. If it didn't start, ",
    successLink: "click here to download",
    successAfter: ". We've also noted your interest and may follow up with rollout guidance.",
    privacy: "We'll only use your email to send the report, plus updates if you tick the box above. Figures are compiled from OpenAI's public pricing pages; verify before purchase.",
    consentLabel: MARKETING_CONSENT_TEXT,
    privacyNotice: "Privacy Notice",
    errNotConfigured: "Download form is not configured right now. Please reach us via WhatsApp or the contact page and we will send the report.",
    errGeneric: "Something went wrong. Please try again, or reach us via WhatsApp.",
  },
  ms: {
    title: "Muat turun laporan pelan ChatGPT (PDF)",
    desc: "Setiap pelan ChatGPT dibandingkan, dari Free hingga Enterprise dan API: harga dalam USD dan RM, seat, kawalan pentadbiran dan keselamatan, serta tetapan lalai latihan data, dalam laporan dua halaman untuk IT dan perolehan. Masukkan butiran anda dan kami akan mulakan muat turun.",
    name: "Nama anda",
    email: "E-mel kerja",
    submit: "Dapatkan laporan",
    sending: "Menghantar…",
    successTitle: "Muat turun anda sedang dihantar",
    successBefore: "Laporan pelan ChatGPT sepatutnya sedang dimuat turun sekarang. Jika ia tidak bermula, ",
    successLink: "klik di sini untuk muat turun",
    successAfter: ". Kami juga telah mencatat minat anda dan mungkin membuat susulan dengan panduan pelaksanaan.",
    privacy: "Kami hanya guna e-mel anda untuk menghantar laporan, serta kemas kini jika anda tandakan kotak di atas. Angka disusun daripada halaman harga awam OpenAI; sahkan sebelum membeli.",
    consentLabel: "Ya, Anchor Sprint boleh menghantar e-mel kepada saya tentang acara, promosi dan produk baharu. Saya boleh berhenti melanggan pada bila-bila masa.",
    privacyNotice: "Notis Privasi",
    errNotConfigured: "Borang muat turun tidak dikonfigurasikan buat masa ini. Sila hubungi kami melalui WhatsApp atau halaman hubungi dan kami akan hantar laporan itu.",
    errGeneric: "Sesuatu tidak kena. Sila cuba lagi, atau hubungi kami melalui WhatsApp.",
  },
  zh: {
    title: "下载 ChatGPT 方案报告（PDF）",
    desc: "从 Free 到 Enterprise 及 API，逐一对比每个 ChatGPT 方案：美元与令吉价格、席位、管理与安全管控，以及数据训练默认设置，整理成两页报告，供 IT 与采购参考。填写资料后即可开始下载。",
    name: "你的姓名",
    email: "工作邮箱",
    submit: "获取报告",
    sending: "发送中…",
    successTitle: "你的下载即将开始",
    successBefore: "ChatGPT 方案报告应该正在下载。如果没有开始，",
    successLink: "点此下载",
    successAfter: "。我们也已记录你的关注，可能会跟进部署建议。",
    privacy: "我们仅用你的邮箱发送报告；若你勾选上方的方框，也会发送最新资讯。数字依据 OpenAI 公开定价页面整理；购买前请核实。",
    consentLabel: "是的，我同意安克极速通过电子邮件向我发送活动、促销和新产品信息。我可随时取消订阅。",
    privacyNotice: "隐私声明",
    errNotConfigured: "下载表单目前尚未配置。请通过 WhatsApp 或联系页面联系我们，我们会把报告发给你。",
    errGeneric: "出了点问题。请重试，或通过 WhatsApp 联系我们。",
  },
};

function triggerDownload() {
  const link = document.createElement("a");
  link.href = PDF_PATH;
  link.download = PDF_FILENAME;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Email-gated download of the ChatGPT plan report (PDF), mirroring the plan
 * report download on the other plans guide. Captures the lead via the
 * SAME Web3Forms endpoint as the contact form, tagged with request_type so these
 * submissions are distinguishable in the inbox, then delivers the PDF. Fires GA
 * events on success (no raw email sent to GA). Locale-aware (en/ms/zh).
 */
export function ChatGPTPlanDownload({
  locale = defaultLocale,
}: {
  locale?: Locale;
}) {
  const t = STRINGS[locale] ?? STRINGS[defaultLocale];
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");

    const web3formsKey = process.env.NEXT_PUBLIC_WEB3FORMS_KEY;
    if (!web3formsKey || web3formsKey === "your_actual_web3forms_key_here") {
      setStatus("error");
      setErrorMsg(t.errNotConfigured);
      return;
    }

    try {
      const body = new FormData();
      body.append("access_key", web3formsKey);
      body.append("name", name);
      body.append("email", email);
      // Distinguishing parameters so plan-download leads stand apart in the inbox.
      body.append("request_type", "Download ChatGPT Plan");
      body.append(
        "message",
        withMarketingConsentLine(
          "I'd like to download the ChatGPT plan report.",
          marketingConsent,
        ),
      );
      body.append("source", SOURCE);
      body.append("subject", `ChatGPT Plan Report download — ${name || email}`);
      body.append("from_name", "Anchor Sprint Website");
      appendMarketingConsent(body, marketingConsent, locale);

      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body,
      });
      const data = await res.json();

      if (!data.success) {
        throw new Error(data.message || "Submission failed");
      }

      // GA: lead captured + file download (no raw email — privacy).
      trackEvent("chatgpt_plan_download_request", {
        category: "lead",
        label: "chatgpt_plan_report_pdf",
        source: SOURCE,
        email_provided: true,
      });
      trackDownload(PDF_FILENAME, "pdf", {
        gated: true,
        source: SOURCE,
      });

      setStatus("success");
      triggerDownload();
    } catch {
      setStatus("error");
      setErrorMsg(t.errGeneric);
    }
  }

  if (status === "success") {
    return (
      <div className="rounded-2xl border border-primary/30 bg-card p-8 text-center">
        <CheckCircle2 className="mx-auto h-10 w-10 text-primary" aria-hidden />
        <h3 className="mt-4 font-display text-xl font-semibold text-neutral-emphasis">
          {t.successTitle}
        </h3>
        <p className="mt-2 text-sm text-neutral-muted">
          {t.successBefore}
          <button
            type="button"
            onClick={triggerDownload}
            className="text-primary underline hover:text-blue-800"
          >
            {t.successLink}
          </button>
          {t.successAfter}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-8">
      <div className="flex items-start gap-3">
        <Download className="mt-0.5 h-6 w-6 shrink-0 text-primary" aria-hidden />
        <div>
          <h3 className="font-display text-xl font-semibold text-neutral-emphasis">
            {t.title}
          </h3>
          <p className="mt-2 text-sm text-neutral-muted">{t.desc}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mt-6">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t.name}
          aria-label={t.name}
          className="rounded-lg border border-border bg-background px-4 py-2.5 text-sm text-neutral-emphasis placeholder:text-neutral-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t.email}
          aria-label={t.email}
          className="rounded-lg border border-border bg-background px-4 py-2.5 text-sm text-neutral-emphasis placeholder:text-neutral-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
        </div>
        <MarketingConsent
          id="chatgpt-plan-report-marketing-consent"
          checked={marketingConsent}
          onChange={setMarketingConsent}
          locale={locale}
          label={t.consentLabel}
          privacyLabel={t.privacyNotice}
          className="mt-4"
        />
        <button
          type="submit"
          disabled={status === "loading"}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis disabled:opacity-60 sm:w-auto"
        >
          {status === "loading" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> {t.sending}
            </>
          ) : (
            <>
              <Download className="h-4 w-4" aria-hidden /> {t.submit}
            </>
          )}
        </button>
      </form>

      {status === "error" && (
        <p className="mt-3 text-sm text-destructive">{errorMsg}</p>
      )}
      <p className="mt-3 text-xs text-neutral-muted">{t.privacy}</p>
    </div>
  );
}
